import React, {
	useState,
	useContext,
	useMemo,
	createContext,
	type ReactNode,
	type Context,
	type ComponentType,
	useCallback,
} from 'react';
import noop from 'lodash/noop';
import { useRelayEnvironment, useMutation, graphql } from 'react-relay';
import { commitLocalUpdate, type RecordSourceSelectorProxy } from 'relay-runtime';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { FavoriteChangeConsumerMutation } from '@atlassian/jira-relay/src/__generated__/FavoriteChangeConsumerMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import RelayDataID from '@atlassian/relay-data-id';
import {
	favoriteMutationToRestPayload,
	favoriteTypeToTypename,
} from '../common/utils/favorite-mutation-to-rest-payload/index.tsx';
import { publishFavorite } from '../common/utils/favorite-pub-sub/index.tsx';
import { PROJECTS_ITEM_TYPE } from '../model/constants.tsx';
import type {
	ChangeFavoritePayload,
	Item,
	Items,
	FavoriteChangeContextType,
	ChangeFavoriteMutationPayload,
	EmitChangedFavorite,
	EmitChangedFavoriteMutation,
} from '../model/types.tsx';
import updateFavorite from '../services/update-favorite.tsx';
import { errorFlag } from './flags/failure/index.tsx';

type State = FavoriteChangeContextType;

export type Props = {
	children: ReactNode;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onShowFlag?: any; // ShowFlagFn ('@atlaskit/flag')
};

export const updateItemFC = (item: Item) => (state: Items) => ({
	...state,
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	[item.type as string]: {
		...state[item.type],
		[item.id]: item,
	},
});

export const updateItem = (item: Item) => (state: State) => ({
	...state,
	items: {
		...state.items,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		[item.type as string]: {
			...state.items[item.type],
			[item.id]: item,
		},
	},
});

export const defaultContextValue: FavoriteChangeContextType = {
	items: {
		boards: {},
		filters: {},
		issues: {},
		dashboards: {},
		projects: {},
		queues: {},
		plans: {},
	},
	changeFavorite: () => new Promise(noop),
	changeFavoriteMutation: noop,
	emitChangedFavorite: noop,
	emitChangedFavoriteMutation: noop,
	updateFavoriteState: noop,
};

export const FavoriteChangeContext: Context<FavoriteChangeContextType> =
	createContext(defaultContextValue);

// supports project types only for now
const useRelayFavoriteUpdate = () => {
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();

	const commitUpdate = useCallback(
		({ id, type, value }: { id: string; type: string; value: boolean }) => {
			if (type === PROJECTS_ITEM_TYPE) {
				commitLocalUpdate(environment, (store) => {
					const ari = createAri({
						resourceOwner: 'jira',
						resourceType: 'project',
						resourceId: id,
						cloudId,
					});
					const projectRecordId = RelayDataID({ id: ari }, 'JiraProject');

					if (projectRecordId) {
						const projectRecord = store.get(projectRecordId);

						const favoriteRecord = projectRecord?.getLinkedRecord('favouriteValue');
						favoriteRecord?.setValue(value, 'isFavourite');
					}
				});
			}
		},
		[cloudId, environment],
	);

	return { commitUpdate };
};

export const FavoriteChangeProvider = ({ onShowFlag, children }: Props) => {
	const { commitUpdate } = useRelayFavoriteUpdate();
	const [items, setItems] = useState<Items>({
		boards: {},
		filters: {},
		dashboards: {},
		issues: {},
		projects: {},
		queues: {},
		plans: {},
	});

	const onChangeStart = useCallback((item: ChangeFavoritePayload) => {
		setItems(updateItemFC({ ...item, pending: true }));
	}, []);

	const onChangeSuccess = useCallback(
		(item: ChangeFavoritePayload, analyticsEvent?: UIAnalyticsEvent) => {
			setItems(updateItemFC({ ...item, pending: false }));
			if (analyticsEvent) {
				fireTrackAnalytics(
					analyticsEvent,
					// removing last letter to make item.type singular tense (i.e. boards => board)
					`${item.type.slice(0, item.type.length - 1)} ${item.value ? 'starred' : 'unstarred'}`,
					`${item.id}`,
				);
			}
		},
		[],
	);

	const onChangeFail = useCallback(
		(item: ChangeFavoritePayload, analyticsId: string, error?: Error) => {
			setItems(updateItemFC({ ...item, value: !item.value, pending: false }));

			if (onShowFlag) {
				onShowFlag(errorFlag());
			}

			fireErrorAnalytics({
				meta: {
					id: analyticsId,
					packageName: 'jiraFavouriteChangeProvider',
					teamName: 'empanada',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		[onShowFlag],
	);

	const changeFavorite = useCallback(
		async (item: ChangeFavoritePayload, analyticsEvent?: UIAnalyticsEvent) => {
			const performChange = async () => {
				onChangeStart(item);

				try {
					commitUpdate({ type: item.type, id: item.id, value: item.value });
					await updateFavorite(item);
					const typename = favoriteTypeToTypename(item.type, item.id);
					if (typename) {
						publishFavorite(typename, {
							type: 'REST',
							entityId: item.id,
							isFavourite: item.value,
						});
					}

					onChangeSuccess(item, analyticsEvent);
				} catch (error: unknown) {
					commitUpdate({ type: item.type, id: item.id, value: !item.value });
					onChangeFail(item, 'changeFavourite', error instanceof Error ? error : undefined);
				}
			};

			return performChange();
		},
		[commitUpdate, onChangeStart, onChangeSuccess, onChangeFail],
	);

	const [commitMutation] = useMutation<FavoriteChangeConsumerMutation>(graphql`
		mutation FavoriteChangeConsumerMutation($input: JiraSetIsFavouriteInput!) {
			jira {
				setEntityIsFavourite(input: $input) {
					success
					favouriteValue {
						id
						isFavourite
					}
				}
			}
		}
	`);

	const changeFavoriteMutation = useCallback(
		(payload: ChangeFavoriteMutationPayload, analyticsEvent?: UIAnalyticsEvent) => {
			const dataId = RelayDataID({ id: payload.id }, payload.typename);
			const restItem = favoriteMutationToRestPayload(payload);

			const onUpdate = (store: RecordSourceSelectorProxy, isOptimistic: boolean) => {
				if (!dataId) {
					return;
				}

				publishFavorite(payload.typename, {
					type: 'MUTATION',
					store,
					dataId,
					entityId: payload.id,
					isFavourite: payload.value,
					isOptimistic,
				});

				const record = store.get(dataId);
				if (record) {
					// Some favoritable entities provide an `isFavourite` field, e.g. `JiraFilter`, `JiraProject`.
					if (typeof record.getValue('isFavourite') === 'boolean') {
						record.setValue(payload.value, 'isFavourite');
					}
					// Optimistically update the `favouriteValue` node associated with the updated entity (if present).
					// We only need to do this during optimistic update as the node will be returned in a successful
					// mutation response.
					if (isOptimistic) {
						const favoriteRecord = record.getLinkedRecord('favouriteValue');
						favoriteRecord?.setValue(payload.value, 'isFavourite');
					}
				}
			};

			onChangeStart(restItem);

			commitMutation({
				variables: {
					input: {
						entityId: payload.id,
						isFavourite: payload.value,
						beforeEntityId: payload.beforeEntity?.id ?? null,
					},
				},
				optimisticUpdater: (store: RecordSourceSelectorProxy) => {
					onUpdate(store, true);
				},
				updater: (store: RecordSourceSelectorProxy, response) => {
					if (response?.jira?.setEntityIsFavourite?.success) {
						onUpdate(store, false);
					}
				},
				onCompleted: (response) => {
					if (response?.jira?.setEntityIsFavourite?.success) {
						onChangeSuccess(restItem, analyticsEvent);
					} else {
						onChangeFail(restItem, 'changeFavoriteMutation');
					}
				},
				onError: (error: Error) => onChangeFail(restItem, 'changeFavouriteMutation', error),
			});
		},
		[commitMutation, onChangeStart, onChangeSuccess, onChangeFail],
	);

	const updateFavoriteState = useCallback((item: ChangeFavoritePayload) => {
		setItems(updateItemFC({ ...item, pending: false }));
	}, []);

	const emitChangedFavorite = useCallback<EmitChangedFavorite>(
		(payload: ChangeFavoritePayload, analyticsEvent?: UIAnalyticsEvent) => {
			onChangeSuccess(payload, analyticsEvent);

			const typeName = favoriteTypeToTypename(payload.type, payload.id);

			if (typeName) {
				publishFavorite(typeName, {
					type: 'REST',
					entityId: payload.id,
					isFavourite: payload.value,
				});
			}
		},
		[onChangeSuccess],
	);

	const emitChangedFavoriteMutation = useCallback<EmitChangedFavoriteMutation>(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(payload: any, store: any, analyticsEvent?: UIAnalyticsEvent) => {
			const dataId = RelayDataID({ id: payload.id }, payload.typename) ?? '';
			const restItem = favoriteMutationToRestPayload(payload);

			onChangeSuccess(restItem, analyticsEvent);

			publishFavorite(payload.typename, {
				type: 'MUTATION',
				store,
				dataId,
				entityId: payload.id,
				isFavourite: payload.value,
				isOptimistic: false,
			});
		},
		[onChangeSuccess],
	);

	const providerValue = useMemo(
		() => ({
			items,
			changeFavorite,
			changeFavoriteMutation,
			emitChangedFavorite,
			emitChangedFavoriteMutation,
			updateFavoriteState,
		}),
		[
			items,
			changeFavorite,
			changeFavoriteMutation,
			emitChangedFavorite,
			emitChangedFavoriteMutation,
			updateFavoriteState,
		],
	);

	return (
		<FavoriteChangeContext.Provider value={providerValue}>
			{children}
		</FavoriteChangeContext.Provider>
	);
};

export const FavoriteChangeConsumer = FavoriteChangeContext.Consumer;

export const withFavoriteChangeConsumer = <
	P,
	W extends P & {
		favoriteChangeContext: FavoriteChangeContextType;
	},
>(
	WrappedComponent: ComponentType<W>,
): ComponentType<P> => {
	const WithFavoriteChangeConsumer = (props: P) => (
		<FavoriteChangeConsumer>
			{(favoriteChangeContext) => (
				// @ts-expect-error - TS2322 - Type 'P & { favoriteChangeContext: FavoriteChangeContextType; }' is not assignable to type 'IntrinsicAttributes & W & { children?: ReactNode; }'.
				<WrappedComponent {...props} favoriteChangeContext={favoriteChangeContext} />
			)}
		</FavoriteChangeConsumer>
	);

	const wrappedDisplayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
	WithFavoriteChangeConsumer.displayName = `WithFavoriteChangeConsumer(${wrappedDisplayName})`;
	return WithFavoriteChangeConsumer;
};

export const useChangeFavorite = () => useContext(FavoriteChangeContext);
